import type { FunctionComponent } from 'react';

import { Action, ResourceType } from '../../../../../../typings/permissions';
import { useHasPermission } from '../../../../../shared/permissions';
import SidebarNavLayout from '../../../../layouts/SidebarNavLayout';
import { shouldDisplayOrganizationPortalTokenSettings } from '../../../../settings/shared/permissions';
import SettingsPage from '../../../../shared/settings/SettingsPage';
import type { ProjectGroup } from '../../../../../../shared/model-typings';
import type { RouterProps } from '../../../../../types';

import ReleaseGroupSettingsContextProvider from './context';

type Props = RouterProps<{ projectGroupId: string }> & {
  //  These come from Redux and is passed down in ProjectGroupContainer
  projectGroup: ProjectGroup;
  fetchProjectGroup: (groupId: string) => Promise<void>;
};

/**
 * ReleaseGroupsSettings is the container for all of the release groups settings pages.
 */
const ReleaseGroupsSettings: FunctionComponent<Props> = ({
  children,
  location,
  params,
  projectGroup,
  fetchProjectGroup,
}) => {
  const { pathname } = location;
  const { projectGroupId } = params;

  const hasPermission = useHasPermission();
  const hasPermissionToViewReportSettings =
    hasPermission({ action: Action.Create, resourceType: ResourceType.ReleaseGroupReportAny })
    || hasPermission({
      action: Action.Create,
      resourceType: ResourceType.ReleaseGroupReport,
      releaseGroupId: Number(projectGroupId),
    });
  const canViewReportSettings = hasPermissionToViewReportSettings;

  const settingsBody = (
    <SettingsPage>
      <ReleaseGroupSettingsContextProvider
        projectGroupId={projectGroupId}
        reportCustomText={projectGroup.reportCustomText ?? null}
        title={projectGroup.title}
        policyId={projectGroup.policyId}
        securityPolicyId={projectGroup.securityPolicyId}
        qualityPolicyId={projectGroup.qualityPolicyId}
        publicOnPortal={projectGroup.publicOnPortal}
        defaultSavedOptionLicensingReport={projectGroup.defaultSavedOptionLicensingReport}
        defaultSavedOptionSbom={projectGroup.defaultSavedOptionSbom}
        onRefetchAfterSave={() => fetchProjectGroup(projectGroupId)}
      >
        {children}
      </ReleaseGroupSettingsContextProvider>
    </SettingsPage>
  );

  const canViewPortalSettings = shouldDisplayOrganizationPortalTokenSettings();
  const shouldShowSidebar = canViewPortalSettings || canViewReportSettings;

  if (!shouldShowSidebar) {
    return (
      <div className="container padding-top-lg">
        {settingsBody}
      </div>
    );
  }

  return (
    <SidebarNavLayout>
      <SidebarNavLayout.Nav pathname={pathname}>
        <SidebarNavLayout.NavHeading>
          Settings
        </SidebarNavLayout.NavHeading>
        <SidebarNavLayout.NavItem to={`/projects/group/${projectGroupId}/settings/general`} activeKey="general">
          General
        </SidebarNavLayout.NavItem>
        {canViewReportSettings && (
          <SidebarNavLayout.NavItem to={`/projects/group/${projectGroupId}/settings/reports`} activeKey="reports">
            Reports
          </SidebarNavLayout.NavItem>
        )}
        {canViewPortalSettings && (
          <SidebarNavLayout.NavItem to={`/projects/group/${projectGroupId}/settings/portal`} activeKey="portal">
            Portal
          </SidebarNavLayout.NavItem>
        )}
      </SidebarNavLayout.Nav>
      <SidebarNavLayout.Content>
        {settingsBody}
      </SidebarNavLayout.Content>
    </SidebarNavLayout>
  );
};

export default ReleaseGroupsSettings;
